import { Controller } from "@hotwired/stimulus";

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Manages the lifecycle of the playground history expand modal:
//   - Open: clone the per-session <template> into the content target, show.
//   - Close: clear content, hide, restore focus.
//   - Backdrop click and ESC dismiss.
//   - On a successful per-cell delete inside the modal (turbo:submit-end),
//     clean up the cell (and the whole card via existing turbo_stream removes
//     fired by the server). If the modal becomes empty, auto-close.
export default class extends Controller<HTMLElement> {
  static targets = ["content", "backdrop"];

  declare readonly contentTarget: HTMLElement;
  declare readonly backdropTarget: HTMLElement;

  private restoreFocusTo: HTMLElement | null = null;

  connect(): void {
    this.handleOpen = this.handleOpen.bind(this);
    this.element.addEventListener("history-expand-modal:open", this.handleOpen);
  }

  disconnect(): void {
    this.element.removeEventListener("history-expand-modal:open", this.handleOpen);
  }

  handleOpen(event: Event): void {
    const detail = (event as CustomEvent).detail as {
      sessionKey: string;
      sourceTile?: HTMLElement | null;
    };
    if (!detail?.sessionKey) return;

    const template = document.getElementById(
      `history_modal_template_${detail.sessionKey}`,
    ) as HTMLTemplateElement | null;
    if (!template) return;

    this.restoreFocusTo = detail.sourceTile ?? null;
    this.contentTarget.replaceChildren(template.content.cloneNode(true));
    this.show();
  }

  close(): void {
    this.hide();
    this.contentTarget.replaceChildren();
    if (this.restoreFocusTo && document.body.contains(this.restoreFocusTo)) {
      this.restoreFocusTo.focus({ preventScroll: true });
    }
    this.restoreFocusTo = null;
  }

  backdropClick(event: MouseEvent): void {
    // Only the backdrop (or the root container itself) triggers a close —
    // clicks bubbling up from inside .history-modal-content are ignored.
    const target = event.target as Node;
    if (target === this.element || target === this.backdropTarget) {
      this.close();
    }
  }

  handleKey(event: KeyboardEvent): void {
    if (event.key === "Escape" && !this.element.classList.contains("hidden")) {
      this.close();
    }
  }

  // Listens to the form_with delete buttons inside the modal. When the server
  // returns 200 (the per-tile turbo_stream removes the cell), we check the
  // remaining cells. If none remain, close the modal — there's nothing left
  // to look at.
  handleDeleteResult(event: Event): void {
    const detail = (event as CustomEvent).detail as { success?: boolean };
    if (!detail?.success) return;
    this.trackEvent("playground-history-deleted");
    // Defer one tick so the Turbo stream remove has applied.
    requestAnimationFrame(() => {
      const remainingCells = this.contentTarget.querySelectorAll("[data-tile-gen-id]");
      if (remainingCells.length === 0) this.close();
    });
  }

  // Fired from the "Open session" link in the modal footer. Tracks the
  // explicit "I want to keep working from this session" intent — distinct
  // from a tile click that opened the modal for preview.
  trackRestoreFromModal(): void {
    this.trackEvent("playground-history-restored", { via: "modal" });
  }

  private trackEvent(name: string, extra: Record<string, unknown> = {}): void {
    try {
      const mode = document.getElementById("playground_history_strip")?.dataset.mode;
      window.umami?.track(name, { mode, ...extra });
    } catch {
      // Telemetry is non-blocking.
    }
  }

  private show(): void {
    this.element.classList.remove("hidden");
    this.element.classList.add("flex");
    this.element.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => {
      this.contentTarget.classList.add("history-modal-enter");
    });
  }

  private hide(): void {
    this.element.classList.add("hidden");
    this.element.classList.remove("flex");
    this.element.setAttribute("aria-hidden", "true");
    this.contentTarget.classList.remove("history-modal-enter");
  }
}
